
import { 
  ModelBase, 
} from "@models/UpsertableBase";
import { 
  IStringHash,
} from "@models";
import { 
  AttackType, 
  DamageType, 
  DataType, 
  EffectType,
  FeatureType
} from "@dndModels/Types";

/**
 * This is a Feature that is unattached.  I'm making a change to how 
 * I'm doing Features, to make it so that Features will have references
 * to the Floating Feature they're based on.  
 * 
 * Floating Features will get points, and regular Features will 
 * inherit them from the Floating Feature.
 * 
 * When modifying a Feature on a Feature Source, it will give an option
 * of whether to make the change on the Floating Feature, or create a 
 * new Floating Feature.
 * 
 * The only part of the Feature that will be saved on the Feature object
 * is the floating_feature_id.
 */
export class FloatingFeature extends ModelBase {
  data_type: DataType = "floating_feature";
  
  feature_type: FeatureType = "";
  feature_points = 1;
  feature_points_from_admin = 0;
  feature_points_from_users: FeatureUserRating[] = [];
  merged_ids: string[] = [];
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      // if (obj.name && obj.name.includes("Spellcasting - Cleric (2024)")) {
      //   debugger;
      // }
      this.feature_type = obj.feature_type ? obj.feature_type : "";
      this.feature_points = obj.feature_points ? obj.feature_points : 1;
      this.feature_points_from_admin = obj.feature_points_from_admin ? obj.feature_points_from_admin : 0;
      this.merged_ids = obj.merged_ids ? obj.merged_ids : [];
      if (this.name.includes(" Of ")) {
        this.name = this.name.replace(" Of ", " of ");
      }
      // if (this.description.includes("DC {dc_string")) {
      //   console.log(this.description);
      // }
      while (this.description.includes("DC {dc_string")) {
        this.description = this.description.replace("DC {dc_string", "{dc_string");
      }
    }
    this.source_books_old = [];
    this.owner_id = "";
  }

  get the_effect_types(): EffectType[] {
    return [];
  }

  get damage_types(): DamageType[] {
    return [];
  }

  get the_attack_types(): AttackType[] {
    return [];
  }

  get the_utility_details(): string[] {
    return [];
  }

  get avg_feature_points(): number {
    return this.feature_points_from_users.length > 0 ? this.feature_points_from_users.reduce((accumulator: number, fp: { feature_points: number, user_id: string }) => {
      return accumulator + fp.feature_points;
    }, 0) / this.feature_points_from_users.length : (this.feature_points_from_admin === 0 ? this.feature_points : this.feature_points_from_admin);
  }

  get_detail_format() {
    const str = `${this.feature_type}`;
    return str.trim();
  }

  toDBObj(): any {
    const base_obj = super.toDBObj();
    return {
      ...base_obj,
      feature_type: this.feature_type,
      merged_ids: this.merged_ids,
      feature_points: this.feature_points,
      feature_points_from_admin: this.feature_points_from_admin,
      source_books: []
    };
  }

  toLight(): any {
    return {
      _id: this._id,
      name: this.name,
      // ...super.toLight(), // Intentionally don't want to include this stuff. FloatingFeature mostly doesn't need that stuff
      feature_type: this.feature_type,
      source_books: []
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.feature_type = copyMe.feature_type;
    this.feature_points = copyMe.feature_points;
    this.feature_points_from_admin = copyMe.feature_points_from_admin;
    this.feature_points_from_users = copyMe.feature_points_from_users;
    if (!drop_ids) {
      this.merged_ids = copyMe.merged_ids;
    }
  }

  safe_copy(copyMe: FloatingFeature): void {
    
  }

  clone(drop_ids = false): FloatingFeature {
    const new_obj = new FloatingFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class FeatureUserRating extends ModelBase {
  data_type: DataType = "feature_user_rating";
  feature_id = "";
  feature_points = 0;
  user_id = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.feature_id = obj.feature_id ? obj.feature_id : "";
      this.feature_points = obj.feature_points ? obj.feature_points : 0;
      this.user_id = obj.user_id ? obj.user_id : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.feature_id = obj.feature_id ? obj.feature_id : "";
  //     this.feature_points = obj.feature_points ? obj.feature_points : 0;
  //     this.user_id = obj.user_id ? obj.user_id : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      feature_id: this.feature_id,
      feature_points: this.feature_points,
      user_id: this.user_id
    };
  }
}

export class IStringHashFeature extends FloatingFeature {
  hash: IStringHash = {};

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.hash = obj.hash ? obj.hash : {};
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.hash = obj.hash ? obj.hash : {};
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      hash: this.hash
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof IStringHashFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: IStringHashFeature) {
    this.hash = {...copyMe.hash};
  }

  clone(drop_ids = false): IStringHashFeature {
    const new_obj = new IStringHashFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class BooleanFeature extends FloatingFeature {
  bool = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.bool = obj.bool ? obj.bool : false;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.bool = obj.bool ? obj.bool : false;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      bool: this.bool
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof BooleanFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: BooleanFeature) {
    this.bool = copyMe.bool;
  }

  clone(drop_ids = false): BooleanFeature {
    const new_obj = new BooleanFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class NumberFeature extends FloatingFeature {
  num = 0;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.num = obj.num ? +obj.num : 0;
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.num = obj.num ? +obj.num : 0;
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      num: this.num
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof NumberFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: NumberFeature) {
    this.num = copyMe.num;
  }

  clone(drop_ids = false): NumberFeature {
    const new_obj = new NumberFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class StringFeature extends FloatingFeature {
  str = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.str = obj.str ? obj.str : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.str = obj.str ? obj.str : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      str: this.str
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof StringFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: StringFeature) {
    this.str = copyMe.str;
  }

  clone(drop_ids = false): StringFeature {
    const new_obj = new StringFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class StringsFeature extends FloatingFeature {
  strings: string[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.strings = obj.strings ? [...obj.strings] : [];
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.strings = obj.strings ? [...obj.strings] : [];
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      strings: this.strings
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof StringsFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: StringsFeature) {
    this.strings = [...copyMe.strings];
  }

  clone(drop_ids = false): StringsFeature {
    const new_obj = new StringsFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class CountAsFeature extends FloatingFeature {
  type = "Ancestry";
  count_as_id = "";
  selectable_key = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.type = obj.type ? obj.type : "Ancestry";
      this.count_as_id = obj.count_as_id ? obj.count_as_id : "";
      this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.type = obj.type ? obj.type : "Ancestry";
  //     this.count_as_id = obj.count_as_id ? obj.count_as_id : "";
  //     this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
  //   }
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      type: this.type,
      count_as_id: this.count_as_id,
      selectable_key: this.selectable_key
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof CountAsFeature) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: CountAsFeature) {
    this.type = copyMe.type;
    this.count_as_id = copyMe.count_as_id;
    this.selectable_key = copyMe.selectable_key;
  }

  clone(drop_ids = false): CountAsFeature {
    const new_obj = new CountAsFeature();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
